import {
  IonHeader,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  type RefresherEventDetail,
  useIonRouter,
  useIonViewWillEnter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { calendarOutline } from "ionicons/icons";
import { ListSkeleton } from "@/components/atoms/skeleton";
import { Content } from "@/components/content";
import { UpcomingApplicationCardOverview } from "@/components/molecules/application/upcoming-application.card.overview";
import { EmptyState } from "@/components/molecules/empty-state";
import { OrganizationDropdown } from "@/components/molecules/organization/organization-dropdown";
import { UpcomingShiftCardRework } from "@/components/molecules/shift/upcoming-shift-card.rework";
import { ForYouNudge } from "@/components/molecules/today/for-you-nudge";
import { SetupNudge } from "@/components/molecules/today/setup-nudge";
import { ShiftTimingBadge } from "@/components/molecules/today/shift-timing-badge";
import { TodayActionItem } from "@/components/molecules/today/today-action-item";
import { Toolbar } from "@/components/toolbar";
import { useAppReworkFlags } from "@/lib/hooks/use-app-rework-flags";
import { useMe } from "@/lib/hooks/use-me";
import { useSetupNudges } from "@/lib/hooks/use-setup-nudges";
import { useUpcomingShiftsOverview } from "@/lib/hooks/use-upcoming-shifts-overview";
import { useForYouNudge } from "@/lib/query/project/use-for-you-nudge";
import { useOutstandingHours } from "@/lib/query/shift/use-outstanding-hours";
import { Routes } from "@/lib/router";
import { upNextItems } from "@/lib/today/up-next";
import { formatDayMonth } from "@/utils/format-datetime";

/** How many further items to preview under "Daarna" before the list becomes a list. */
const UP_NEXT_LIMIT = 3;

/**
 * Vandaag — the front door.
 *
 * An employee opens this app to answer three questions: when do I work, where do I have to be, and
 * did my hours come through. Everything on this screen answers one of those, in that order.
 * Anything else belongs on another tab.
 */
export function TodayPage() {
  const { t } = useTranslation();
  const router = useIonRouter();
  const { me } = useMe();

  const {
    items,
    isLoading: isLoadingOverview,
    refetch: refetchOverview,
  } = useUpcomingShiftsOverview();
  const outstandingHours = useOutstandingHours();

  // Werk waarop deze medewerker zich kan aanmelden. Achter dezelfde vlag als "Voor jou" op
  // Ontdek: het is dezelfde belofte met dezelfde twee regels, dus één schakelaar hoort beide
  // aan of uit te zetten.
  const { forYou: forYouEnabled } = useAppReworkFlags();
  const forYouNudge = useForYouNudge({ enabled: forYouEnabled });

  // Meldingen en een gekoppelde agenda: één keer instellen, daarna nooit meer. Zie
  // `use-setup-nudges` voor waarom dit een kaart is en geen venster bij het opstarten.
  const setupNudges = useSetupNudges();

  // The hero is the next *confirmed* shift. Applications are not a place to be, so they
  // never take the top slot — they show up under "Daarna" with their own card. A shift
  // without both times cannot answer "when do I work", so it stays in the list instead of
  // claiming the hero slot and rendering nothing.
  const heroItem = items.find(
    (item) => item.type === "shift" && !!item.shift.startAt && !!item.shift.endAt,
  );
  const upNext = upNextItems(items, { exclude: heroItem, limit: UP_NEXT_LIMIT });

  const firstName = me?.name?.trim().split(" ")[0];

  const refetchAll = async () => {
    // Deliberately only the queries this screen actually renders. The other tabs
    // blanket-refetch nine query keys on every view-enter, which is what makes the app feel
    // like it is hanging on a bad connection.
    await Promise.all([refetchOverview(), outstandingHours.refetch(), forYouNudge.refetch()]);
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await refetchAll();
    event.detail.complete();
  };

  useIonViewWillEnter(() => {
    void refetchAll();
  });

  const showEmptyState = !isLoadingOverview && !heroItem && upNext.length === 0;

  const forYouSection = (
    <ForYouNudge
      matches={forYouNudge.matches}
      onSeeAll={() => router.push(Routes.Projects(), "forward")}
    />
  );

  // Onder het werk en niet erboven: dit gaat over de app, niet over je dag. Wel op Vandaag en niet
  // weggestopt in de instellingen, want daar komt niemand die niet al weet dat hij er iets moet.
  const setupSection = (
    <SetupNudge
      steps={setupNudges.steps}
      missingFields={setupNudges.missingFields}
      onDismiss={setupNudges.dismiss}
    />
  );

  return (
    <IonPage>
      <IonHeader>
        <Toolbar className="ion-no-border plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]">
          <div className="flex items-center gap-3">
            {/* De begroeting is de enige titel in de app met een naam erin, en dus de enige die kan
                uitlopen. Afkappen is lelijk; de wisselaar van het scherm duwen is erger. */}
            <h1 className="min-w-0 truncate text-2xl font-semibold leading-[1.2] text-txt-primary">
              {firstName
                ? t("routes:today.greeting", { name: firstName })
                : t("routes:today.greeting-anonymous")}
            </h1>
            <OrganizationDropdown />
          </div>
        </Toolbar>
      </IonHeader>

      <Content scrollUnderChrome className="part-scroll:gap-8">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>

        {/* Zolang het overzicht onderweg is stond hier niets: een leeg scherm dat na een paar
            tellen in één keer volklapte. Nu de vorm van wat eraan komt, en dus ook alvast de
            hoogte -- het scherm springt bij aankomst niet meer op. */}
        {isLoadingOverview && <ListSkeleton count={3} />}

        {heroItem?.type === "shift" && heroItem.shift.startAt && heroItem.shift.endAt && (
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-xs font-bold uppercase tracking-wide text-txt-secondary">
                {t("routes:today.next-shift")}
              </h2>
              {/* Het aftellen klopt sowieso — dat rekent met absolute momenten — maar het uur dat
                  de badge noemt ("morgen 18:00") hoort in projecttijd te staan. */}
              <ShiftTimingBadge
                startAt={heroItem.shift.startAt}
                endAt={heroItem.shift.endAt}
                timeZone={heroItem.shift.timeZone ?? heroItem.shift.project?.timeZone}
              />
            </div>
            <UpcomingShiftCardRework shift={heroItem.shift} variant="hero" />
          </section>
        )}

        {outstandingHours.count > 0 && (
          <section className="flex flex-col gap-3">
            <h2 className="text-xs font-bold uppercase tracking-wide text-txt-secondary">
              {t("routes:today.waiting-for-you")}
            </h2>

            {outstandingHours.count > 0 && (
              <TodayActionItem
                tone="waiting"
                title={t("routes:today.hours.title", { count: outstandingHours.count })}
                hint={
                  outstandingHours.oldest?.startAt
                    ? t("routes:today.hours.hint", {
                        date: formatDayMonth(outstandingHours.oldest.startAt, {
                          timeZone:
                            outstandingHours.oldest.timeZone ??
                            outstandingHours.oldest.project?.timeZone,
                        }),
                      })
                    : undefined
                }
                onClick={() => router.push(Routes.CompletedShifts(), "forward")}
              />
            )}
          </section>
        )}

        {/*
          Boven "Daarna" en niet eronder: dat is een lijst kaarten, en wat daaronder komt scrollt
          weg. Onder de hero, want die beantwoordt "wanneer werk ik" en dáárvoor gaat de app open.
          Staat er niets gepland, dan verhuist hij naar onder de lege staat — daar is hij het
          antwoord op "je staat nergens ingepland" en niet het voorwoord erop.
        */}
        {!showEmptyState && forYouSection}
        {!showEmptyState && setupSection}

        {upNext.length > 0 && (
          <section className="flex flex-col gap-5">
            <h2 className="text-xs font-bold uppercase tracking-wide text-txt-secondary">
              {t("routes:today.up-next")}
            </h2>
            {upNext.map((item) =>
              item.type === "shift" ? (
                <UpcomingShiftCardRework key={item.shift.id} shift={item.shift} />
              ) : (
                <UpcomingApplicationCardOverview
                  key={item.application.id}
                  application={item.application}
                />
              ),
            )}
          </section>
        )}

        {showEmptyState && (
          <>
            <div className="flex flex-col items-center gap-4">
              <EmptyState
                icon={calendarOutline}
                title={t("routes:today.empty.title")}
                description={t("routes:today.empty.description")}
              />
              {/*
                Concreet werk slaat een knop naar een lijst. Alleen als we niets te suggereren
                hebben blijft de algemene weg naar Ontdek staan.
              */}
              {forYouNudge.matches.length === 0 && (
                <button
                  type="button"
                  onClick={() => router.push(Routes.Projects(), "forward")}
                  className="rounded-xl border-2 border-solid border-button-primary bg-button-primary px-5 py-3 text-md font-semibold leading-[1.5] text-txt-inverted"
                >
                  {t("routes:today.empty.cta")}
                </button>
              )}
            </div>

            {forYouSection}
            {setupSection}
          </>
        )}
      </Content>
    </IonPage>
  );
}
